import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, MessageCircle, Check } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

/**
 * InquiryDialog — modal that lets a buyer start an in-platform chat with the
 * producer for an "inquiry" ticket tier (e.g. mesa a consultar). Replaces the
 * previous external WhatsApp/DM link.
 *
 * After submission, the buyer is shown a confirmation with a personal link
 * (saved in localStorage) so they can come back to read the producer reply.
 */
export default function InquiryDialog({ open, onClose, event, tier, accentColor = '#D4AF37' }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const reset = () => {
    setName(''); setEmail(''); setPhone(''); setMessage('');
    setSubmitting(false); setSuccessData(null);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error('Completá nombre, email y mensaje');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/inquiries', {
        event_id: event.id,
        tier_id: tier.id,
        buyer_name: name.trim(),
        buyer_email: email.trim(),
        buyer_phone: phone.trim() || null,
        message: message.trim(),
      });
      // Persist token for follow-up
      const tokens = JSON.parse(localStorage.getItem('bravo_inquiry_tokens') || '{}');
      tokens[res.data.inquiry_id] = {
        token: res.data.token,
        event_title: event.title,
        tier_name: tier.name,
        created_at: new Date().toISOString(),
      };
      localStorage.setItem('bravo_inquiry_tokens', JSON.stringify(tokens));
      setSuccessData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar la consulta');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !tier) return null;

  const followUrl = successData ? `${window.location.origin}/consultas/${successData.token}` : '';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
        className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md"
        data-testid="inquiry-dialog"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 12 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-md rounded-2xl bg-zinc-950 border border-white/10 overflow-hidden"
        >
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-3 right-3 p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white z-10"
            data-testid="inquiry-dialog-close"
          >
            <X size={16} />
          </button>

          {!successData ? (
            <>
              <div className="px-6 pt-6 pb-4 border-b border-white/5" style={{ background: `linear-gradient(180deg, ${accentColor}25 0%, transparent 100%)` }}>
                <div className="flex items-center gap-2 mb-1">
                  <MessageCircle size={16} style={{ color: accentColor }} />
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: accentColor }}>Consulta directa con la productora</p>
                </div>
                <h3 className="text-lg font-bold text-white">{tier.name}</h3>
                <p className="text-xs text-white/50 mt-0.5">{event.title}</p>
              </div>

              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">Nombre *</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:border-white/30 focus:outline-none"
                      placeholder="Tu nombre"
                      data-testid="inquiry-name"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">Teléfono</label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:border-white/30 focus:outline-none"
                      placeholder="+54 9..."
                      data-testid="inquiry-phone"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">Email *</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:border-white/30 focus:outline-none"
                    placeholder="tu@email.com"
                    data-testid="inquiry-email"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">Mensaje *</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    rows={4}
                    className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:border-white/30 focus:outline-none resize-none"
                    placeholder="Hola, me interesa esta opción. ¿Pueden contarme más?"
                    data-testid="inquiry-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="inquiry-submit"
                  className="w-full py-3 rounded-lg font-bold uppercase tracking-wider text-sm transition-opacity disabled:opacity-50"
                  style={{ background: accentColor, color: '#000' }}
                >
                  {submitting ? 'Enviando…' : (<><Send size={14} className="inline mr-1.5" /> Enviar consulta</>)}
                </button>
                <p className="text-[10px] text-white/35 leading-relaxed text-center pt-1">
                  Iniciás un chat dentro de la plataforma con la productora. Te avisaremos por email cuando respondan.
                </p>
              </form>
            </>
          ) : (
            <div className="px-6 py-7 text-center">
              <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: `${accentColor}25`, border: `2px solid ${accentColor}` }}>
                <Check size={26} style={{ color: accentColor }} />
              </div>
              <h3 className="text-lg font-bold text-white mb-1">¡Consulta enviada!</h3>
              <p className="text-sm text-white/60 mb-4 leading-relaxed">
                La productora va a responderte por acá. Guardá este link para volver a ver las respuestas:
              </p>
              <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-4">
                <a
                  href={followUrl}
                  className="block text-xs font-mono break-all hover:underline"
                  style={{ color: accentColor }}
                  data-testid="inquiry-follow-link"
                >{followUrl}</a>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { navigator.clipboard?.writeText(followUrl); toast.success('Link copiado'); }}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-white/5 hover:bg-white/10 text-white border border-white/10"
                >
                  Copiar link
                </button>
                <a
                  href={followUrl}
                  className="flex-1 py-2.5 rounded-lg text-sm font-bold text-center"
                  style={{ background: accentColor, color: '#000' }}
                >
                  Abrir chat
                </a>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
